import s from './CTABar.module.scss'
import cn from 'clsx'
import { FC, useEffect, useRef, useState } from 'react'
import { Button, Text } from '@components/ui'
import Image from 'next/image'
import { LoreThreeDetailPage } from '@generated/model/loreThreeDetailPage'
import Link from 'next/link'
import type { Product } from '@commerce/types/product'
import { OffRoadSmall, RoadSmall } from '@components/icons'

type CTABarProps = {
  product: Product
  page: LoreThreeDetailPage
}

const CTABar: FC<CTABarProps> = ({ product, page }) => {
  const [isInview, setIsInview] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const image = page.hero?.image

  useEffect(() => {
    if (!ref.current) return

    const onScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset
      const viewportH = window.innerHeight
      const docH = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight
      )

      // threshold when entering the last 50vh of the page
      const threshold = docH - viewportH * 0.5

      const viewportHeight = window.innerHeight

      if (scrollY + viewportH >= threshold) {
        setIsInview(false)
      } else if (scrollY + viewportHeight * 0.66 > viewportHeight) {
        setIsInview(true)
      } else {
        setIsInview(false)
      }
    }

    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [ref])

  return (
    <>
      <div className={cn(s.root, isInview ? s.active : '')} ref={ref}>
        <div className={s.productImage}>
          {image && (
            <Image
              src={image as unknown as string}
              alt={s.title}
              width={50}
              height={50}
              layout="responsive"
              objectFit="contain"
            />
          )}
        </div>
        <div className={s.icon}>
          {(page.color_scheme as unknown as string) == 'green' ? (
            <OffRoadSmall className={s.icon} />
          ) : (
            <RoadSmall className={s.icon} />
          )}
        </div>
        <div className={s.title}>
          <Text variant="smallText" className={s.titleText}>
            {page.heading}
          </Text>
          <Text variant="smallText" className={s.titleText}>
            {page.variant_title}
          </Text>
        </div>
        <div className={s.buttonContainer}>
          <Link href={page.hero?.order_link || ''}>
            <a className={s.button}>
              <Button
                Component="span"
                className={s.buttonInner}
                variant="white"
              >
                Order Now
              </Button>
            </a>
          </Link>
        </div>
      </div>
    </>
  )
}

export default CTABar
